import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguageCurrency, formatPhoneNumber } from '../context/LanguageCurrencyContext';

const Terms = () => {
  const { language } = useLanguageCurrency();

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B', width: '100%' }}>
      <Navbar />

      {/* Hero Section */}
      <section 
        className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
        style={{ 
          background: 'linear-gradient(135deg, rgba(29, 42, 59, 1) 0%, rgba(29, 42, 59, 0.98) 30%, rgba(213, 181, 119, 0.08) 70%, rgba(29, 42, 59, 0.95) 100%)',
          width: '100%',
          minHeight: 'auto',
        }}
      >
        <div className="w-full max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <div className="inline-block mb-6">
              <div className="w-20 h-1 mx-auto mb-3 rounded-full animate-line-expand" style={{ 
                background: 'linear-gradient(90deg, transparent, #D5B577, transparent)',
                boxShadow: '0 0 10px rgba(213, 181, 119, 0.5)'
              }}></div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight relative flex items-center justify-center flex-wrap gap-2 sm:gap-4" style={{ 
              textShadow: '0 0 30px rgba(213, 181, 119, 0.3), 0 4px 8px rgba(0, 0, 0, 0.3)'
            }}>
              <span className="relative inline-flex items-center justify-center gap-2 sm:gap-4">
                {/* Terms Icon - Document/File Icon */}
                <svg 
                  className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 animate-icon-float"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  style={{
                    color: '#D5B577',
                    filter: 'drop-shadow(0 0 10px rgba(213, 181, 119, 0.5))'
                  }}
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                  />
                </svg>
                {/* Animated Text */}
                <span className="terms-text">
                  {language === 'ar' ? (
                    <span
                      style={{
                        background: 'linear-gradient(135deg, #D5B577 0%, #f5d89a 50%, #D5B577 100%)',
                        backgroundSize: '200% 200%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        animation: 'gradient-shift-once 3s ease-in-out forwards',
                      }}
                    >
                      {language === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions'}
                    </span>
                  ) : (
                    (language === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions').split('').map((char, index) => (
                      <span
                        key={index}
                        className="inline-block"
                        style={{
                          animationDelay: `${index * 0.08}s`,
                          background: 'linear-gradient(135deg, #D5B577 0%, #f5d89a 50%, #D5B577 100%)',
                          backgroundSize: '200% 200%',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          animation: 'gradient-shift-once 3s ease-in-out forwards',
                        }}
                      >
                        {char === ' ' ? '\u00A0' : char}
                      </span>
                    ))
                  )}
                </span>
                {/* Animated underline */}
                <div className="absolute -bottom-2 left-0 right-0 h-0.5 animate-underline-expand" style={{
                  background: 'linear-gradient(90deg, transparent, #D5B577, transparent)',
                  opacity: 0.8
                }}></div>
                {/* Glow effect */}
                <div className="absolute inset-0 animate-pulse-glow" style={{
                  background: 'radial-gradient(circle, rgba(213, 181, 119, 0.2) 0%, transparent 70%)',
                  filter: 'blur(20px)',
                  zIndex: -1
                }}></div>
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-2xl mx-auto leading-relaxed font-light animate-fade-in-up" style={{
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              animationDelay: '0.8s',
              animationFillMode: 'both'
            }}>
              {language === 'ar' ? 'يرجى قراءة هذه الشروط بعناية قبل استخدام خدماتنا' : 'Please read these terms carefully before using our services'}
            </p>
            <div className="w-20 h-1 mx-auto mt-6 rounded-full animate-line-expand-delayed" style={{ 
              background: 'linear-gradient(90deg, transparent, #D5B577, transparent)',
              boxShadow: '0 0 10px rgba(213, 181, 119, 0.5)'
            }}></div>
            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={() => setLanguage('ar')}
                className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                  language === 'ar' 
                    ? 'bg-[#D5B577] text-[#1D2A3B]' 
                    : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                }`}
              >
                العربية
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                  language === 'en' 
                    ? 'bg-[#D5B577] text-[#1D2A3B]' 
                    : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                }`}
              >
                English
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1D2A3B', width: '100%' }}>
        <div className="w-full max-w-4xl mx-auto">
          <div className="bg-slate-800/50 rounded-2xl p-6 sm:p-8 md:p-10 shadow-xl border border-amber-500/20" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            
            {/* Definitions */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? 'أولاً: التعريفات' : '1. Definitions'}
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4" style={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
                {language === 'ar' 
                  ? 'الشركة: هوستا جلوبال للتنمية العقارية. المالك: المالك القانوني للوحدة المسجلة لدى الشركة. الضيف: مستأجر الوحدة ومن يرافقه أثناء الإقامة. النسخة العربية هي المرجع المعتمد.'
                  : 'Company: Hosta Global Real Estate Development. Owner: Legal owner of the registered property. Guest: Tenant and accompanying persons. The Arabic version prevails in case of conflict.'}
              </p>
            </div>

            {/* Asset Management Agreement - Owner Section */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? 'ثانيًا: إدارة وتشغيل الأصول (بين الشركة والمالك)' : '2. Asset Management Agreement (Company & Owner)'}
              </h2>
              
              <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? '1. التفويض والإدارة' : '2.1 Authorization'}
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>يمنح المالك الشركة تفويضًا حصريًا لإدارة، تشغيل، تسويق وتأجير الوحدة.</li>
                    <li>للشركة تحصيل الإيجارات وإدارة المدفوعات نيابة عن المالك.</li>
                    <li>يحق للشركة تعديل الأسعار وسياسات التشغيل وفق متطلبات السوق مع إخطار المالك.</li>
                  </>
                ) : (
                  <>
                    <li>The Owner grants the Company exclusive rights to manage, operate, market, and lease the property.</li>
                    <li>The Company may collect rents and manage payments on behalf of the Owner.</li>
                    <li>Pricing and operational policies may be adjusted according to market conditions with notification.</li>
                  </>
                )}
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6" style={{ color: '#D5B577' }}>
                {language === 'ar' ? '2. فترة السماح' : '2.2 Grace Period'}
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>فترة تجهيز لا تتجاوز شهرًا من تاريخ استلام الوحدة جاهزة للتشغيل.</li>
                    <li>لا يحق للمالك المطالبة بعوائد خلال هذه الفترة.</li>
                    <li>لا يجوز الإلغاء خلال فترة السماح إلا بموافقة الشركة.</li>
                    <li>في حال الإلغاء دون موافقة، يتحمل المالك جميع التكاليف.</li>
                  </>
                ) : (
                  <>
                    <li>A preparation period of up to one month from handover.</li>
                    <li>No revenue entitlement during this period.</li>
                    <li>Cancellation is not allowed without written approval.</li>
                    <li>Any unauthorized cancellation requires full cost reimbursement.</li>
                  </>
                )}
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6" style={{ color: '#D5B577' }}>
                {language === 'ar' ? '3. الالتزامات المالية' : '2.3 Financial Obligations'}
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>يلتزم المالك بسداد الضرائب والرسوم القانونية.</li>
                    <li>تسدد الشركة فواتير المرافق وتُخصم من عوائد المالك.</li>
                  </>
                ) : (
                  <>
                    <li>The Owner bears all taxes and legal fees.</li>
                    <li>Utilities are paid by the Company and deducted from revenues.</li>
                  </>
                )}
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6" style={{ color: '#D5B577' }}>
                {language === 'ar' ? '4. حدود المسؤولية' : '2.4 Liability Limitations'}
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>الشركة غير مسؤولة عن العيوب السابقة أو أخطاء المالك أو القوة القاهرة.</li>
                    <li>الحد الأقصى للمسؤولية لا يتجاوز صافي عائد شهر واحد.</li>
                  </>
                ) : (
                  <>
                    <li>The Company is not liable for prior defects, Owner negligence, or force majeure.</li>
                    <li>Maximum liability is limited to one month of net revenue.</li>
                  </>
                )}
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6" style={{ color: '#D5B577' }}>
                {language === 'ar' ? '5. استخدام المالك للوحدة' : '2.5 Owner Usage'}
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>يحق للمالك استخدام وحدته لمدة شهر سنويًا كحد أقصى وفق ضوابط المواسم.</li>
                    <li>تخصم المصاريف الثابتة أثناء الاستخدام.</li>
                  </>
                ) : (
                  <>
                    <li>Owner may use the unit up to one month per year, subject to seasonal limits.</li>
                    <li>Fixed costs may be deducted.</li>
                  </>
                )}
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6" style={{ color: '#D5B577' }}>
                {language === 'ar' ? '6. الحساب الإلكتروني' : '2.6 Owner Account'}
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>لكل مالك حساب إلكتروني لمتابعة الحجوزات والإيرادات والمصروفات.</li>
                    <li>البيانات معلوماتية ولا تُعد مستندات محاسبية نهائية.</li>
                  </>
                ) : (
                  <>
                    <li>Digital account provided for monitoring bookings, revenues, and expenses.</li>
                    <li>Data is informational unless officially approved.</li>
                  </>
                )}
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6" style={{ color: '#D5B577' }}>
                {language === 'ar' ? '7. إنهاء التعاقد' : '2.7 Termination'}
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>يمكن للمالك طلب الإنهاء بعد فترة السماح.</li>
                    <li>لا يؤثر الإنهاء على الحجوزات القائمة أو المستحقات السابقة.</li>
                  </>
                ) : (
                  <>
                    <li>Termination may be requested after the grace period.</li>
                    <li>Existing obligations and bookings remain valid.</li>
                  </>
                )}
              </ul>
            </div>

            {/* Guest Stay Rules */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? 'ثالثًا: شروط الإقامة (بين الشركة والضيف)' : '3. Guest Stay Rules (Company & Guest)'}
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>تقديم أصل الهوية عند الوصول.</li>
                    <li>الوحدة ومحتوياتها تحت مسؤولية الضيف.</li>
                    <li>رد التأمين خلال 21 يومًا بعد خصم التلفيات (بالجنيه المصري).</li>
                    <li>موعد الدخول 3:00 مساءً – الخروج 12:00 ظهرًا.</li>
                    <li>خدمات النظافة برسوم إضافية.</li>
                    <li>في حال عدم الإخلاء يتحمل الضيف كافة التكاليف والتعويضات.</li>
                    <li>الشركة غير مسؤولة عن الأمتعة الشخصية.</li>
                  </>
                ) : (
                  <>
                    <li>Valid ID must be presented upon check-in.</li>
                    <li>Guest is responsible for the unit and contents.</li>
                    <li>Security deposit refunded within 21 days after deductions (EGP only).</li>
                    <li>Check-in: 3:00 PM – Check-out: 12:00 PM.</li>
                    <li>Cleaning services available with extra charges.</li>
                    <li>Late checkout or refusal to vacate results in full charges and compensation.</li>
                    <li>Company is not responsible for personal belongings.</li>
                  </>
                )}
              </ul>
            </div>

            {/* General Provisions */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? 'رابعًا: أحكام عامة' : '4. Final Provisions'}
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>تخضع الشروط لقوانين جمهورية مصر العربية.</li>
                    <li>الاختصاص لمحاكم القاهرة.</li>
                    <li>بطلان أي بند لا يؤثر على باقي البنود.</li>
                  </>
                ) : (
                  <>
                    <li>Governed by the laws of the Arab Republic of Egypt.</li>
                    <li>Cairo courts have jurisdiction.</li>
                    <li>Invalid clauses do not affect remaining clauses.</li>
                  </>
                )}
              </ul>
            </div>

            {/* Contact Information */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? 'معلومات الاتصال' : 'Contact Us'}
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4" style={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
                {language === 'ar' ? 'إذا كان لديك أي أسئلة حول هذه الشروط والأحكام، يرجى الاتصال بنا:' : 'If you have any questions about these Terms and Conditions, please contact us:'}
              </p>
              <div className="bg-slate-700/50 rounded-lg p-4 mt-4">
                <p className="text-gray-300 mb-2" style={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
                  <strong style={{ color: '#D5B577' }}>{language === 'ar' ? 'البريد الإلكتروني:' : 'Email:'}</strong> support@hostaglobal.com
                </p>
                <p className="text-gray-300 mb-2" style={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
                  <strong style={{ color: '#D5B577' }}>{language === 'ar' ? 'الهاتف:' : 'Phone:'}</strong> {formatPhoneNumber('+20 100 000 0000', language)}
                </p>
                <p className="text-gray-300" style={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
                  <strong style={{ color: '#D5B577' }}>{language === 'ar' ? 'العنوان:' : 'Address:'}</strong> {language === 'ar' ? 'القاهرة، مصر' : 'Cairo, Egypt'}
                </p>
              </div>
            </div>

            {/* Acknowledgment */}
            <div className="mt-10 pt-6 border-t border-gray-700">
              <p className="text-gray-400 text-sm text-center" style={{ textAlign: language === 'ar' ? 'right' : 'center' }}>
                {language === 'ar' 
                  ? 'باستخدام خدمات هوستا جلوبال، فإنك تقر بأنك قد قرأت وفهمت ووافقت على الالتزام بهذه الشروط والأحكام.'
                  : 'By using HOSTA GLOBAL\'s services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      
      {/* CSS Animations */}
      <style>{`
        @keyframes gradient-shift-once {
          0% { 
            background-position: 0% 50%;
            transform: translateY(0);
            opacity: 0;
          }
          50% { 
            background-position: 100% 50%;
            transform: translateY(-3px);
            opacity: 1;
          }
          100% { 
            background-position: 0% 50%;
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes underline-expand {
          0% { 
            width: 0;
            opacity: 0;
          }
          50% {
            opacity: 0.8;
          }
          100% { 
            width: 100%;
            opacity: 0.8;
          }
        }
        
        @keyframes pulse-glow {
          0%, 100% { 
            opacity: 0.3;
            transform: scale(1);
          }
          50% { 
            opacity: 0.6;
            transform: scale(1.1);
          }
        }
        
        @keyframes line-expand {
          0% { 
            width: 0;
            opacity: 0;
          }
          100% { 
            width: 80px;
            opacity: 1;
          }
        }
        
        @keyframes line-expand-delayed {
          0% { 
            width: 0;
            opacity: 0;
          }
          50% {
            opacity: 0;
          }
          100% { 
            width: 80px;
            opacity: 1;
          }
        }
        
        @keyframes fade-in-up {
          0% { 
            opacity: 0;
            transform: translateY(20px);
          }
          100% { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes icon-float {
          0%, 100% { 
            transform: translateY(0) rotate(0deg);
          }
          50% { 
            transform: translateY(-8px) rotate(5deg);
          }
        }
        
        .terms-text span {
          animation: letter-float-once 2s ease-out forwards;
        }
        
        @keyframes letter-float-once {
          0% { 
            transform: translateY(20px) rotate(0deg);
            opacity: 0;
          }
          100% { 
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
        }
        
        .animate-line-expand {
          animation: line-expand 1.5s ease-out forwards;
        }
        
        .animate-underline-expand {
          animation: underline-expand 1.2s ease-out 0.5s forwards;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }
        
        .animate-line-expand-delayed {
          animation: line-expand-delayed 1.5s ease-out 1s forwards;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
          opacity: 0;
        }
        
        .animate-icon-float {
          animation: icon-float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Terms;
